import axios from "axios";
import { IApiResponse, IError } from "models";

export const BASE_URL = "https://dev-api.emuntransport.com/api/v1/";
export const WEBSOCKET_URL = "https://dev-api.emuntransport.com";

// # Map it explicitly to the dev sub-domains you plan on assigning in NGINX.
// REACT_APP_API_URL="https://dev-api.emuntransport.com/api/v1"
// REACT_APP_ADMIN_URL="https://dev-admin.emuntransport.com"


const defaultError: IError = {
  errors: ["Network error"],
  message: "Network error",
  status: 400,
};

// --- EMUN PERFORMANCE CACHE ---
// Simple Zero-Dependency Global Cache for Admin Panel Efficiency
const apiCache = new Map<string, { data: any; timestamp: number }>();
const DEFAULT_STALE_TIME = 30000; // 30 seconds

export const clearApiCache = () => apiCache.clear();

function handleAuthorizationError(error: any): IError {
  // Only force logout for 401 (unauthenticated). For 403 (forbidden) we should
  // surface the error but keep the user logged in to avoid unexpected logouts
  // for valid users with insufficient privileges on a specific endpoint.
  if (error?.response?.status === 401) {
    localStorage.removeItem("token");
    window.location.href = "/login";
  }

  if (error?.response?.data) {
    const errorResponse = error.response.data;
    if (!Array.isArray(errorResponse) && typeof errorResponse !== "string") {
      if (errorResponse.detail) {
        if (typeof errorResponse.detail === "string")
          return {
            errors: [errorResponse.detail],
            message: errorResponse.detail,
            status: error.response.status,
          };
        else if (Array.isArray(errorResponse.detail)) {
          const errors = errorResponse.detail.map(
            (item: any) => `${item.loc[item.loc.length - 1]}: ${item.msg}`
          );
          return {
            errors,
            message: errors.join("\n "),
            status: error.response.status,
          };
        }
      } else if (
        !Array.isArray(errorResponse.message) &&
        typeof errorResponse.message === "string"
      )
        return {
          errors: [errorResponse.message],
          message: errorResponse.message,
          status: error.response.status,
        };
    } else if (typeof errorResponse === "string")
      return {
        errors: [errorResponse],
        message: errorResponse,
        status: error.response.status,
      };
  }

  return defaultError;
}

export function publicGetRequest<T>(url: string): Promise<IApiResponse<T>> {
  return new Promise((resolve) => {
    axios
      .get(url)
      .then((response) => resolve({ data: response.data }))
      .catch((error) => {
        resolve({ error: handleAuthorizationError(error) });
      });
  });
}

export function getRequest<T>(url: string): Promise<IApiResponse<T>> {
  return new Promise((resolve) => {
    axios
      .get(`${BASE_URL}${url}`)
      .then((response) => resolve({ data: response.data }))
      .catch((error) => {
        resolve({ error: handleAuthorizationError(error) });
      });
  });
}

export function postRequest<T>(
  url: string,
  data: any
): Promise<IApiResponse<T>> {
  return new Promise((resolve) => {
    axios
      .post(`${BASE_URL}${url}`, data)
      .then((response) => resolve({ data: response.data }))
      .catch((error) => {
        resolve({ error: handleAuthorizationError(error) });
      });
  });
}

export function authorizeGetRequest<T>(
  url: string,
  useCache: boolean = false
): Promise<IApiResponse<T>> {
  // Check cache first
  if (useCache) {
    const cached = apiCache.get(url);
    const now = Date.now();
    if (cached && now - cached.timestamp < DEFAULT_STALE_TIME) {
      return Promise.resolve({ data: cached.data });
    }
  }

  return new Promise((resolve) => {
    axios
      .get(`${BASE_URL}${url}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      .then((response) => {
        // Update cache
        if (useCache) {
          apiCache.set(url, { data: response.data, timestamp: Date.now() });
        }
        resolve({ data: response.data });
      })
      .catch((error) => {
        resolve({ error: handleAuthorizationError(error) });
      });
  });
}

export function authorizePostRequest<T>(
  url: string,
  data: any
): Promise<IApiResponse<T>> {
  return new Promise((resolve) => {
    axios
      .post(`${BASE_URL}${url}`, data, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      .then((response) => resolve({ data: response.data }))
      .catch((error) => {
        resolve({ error: handleAuthorizationError(error) });
      });
  });
}

export function authorizePutRequest<T>(
  url: string,
  data: any
): Promise<IApiResponse<T>> {
  return new Promise((resolve) => {
    axios
      .put(`${BASE_URL}${url}`, data, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      .then((response) => resolve({ data: response.data }))
      .catch((error) => {
        resolve({ error: handleAuthorizationError(error) });
      });
  });
}

export function authorizePatchRequest<T>(
  url: string,
  data: any
): Promise<IApiResponse<T>> {
  return new Promise((resolve) => {
    axios
      .patch(`${BASE_URL}${url}`, data, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      .then((response) => resolve({ data: response.data }))
      .catch((error) => {
        resolve({ error: handleAuthorizationError(error) });
      });
  });
}

export function authorizeDeleteRequest<T>(
  url: string
): Promise<IApiResponse<T>> {
  return new Promise((resolve) => {
    axios
      .delete(`${BASE_URL}${url}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      .then((response) => resolve({ data: response.data }))
      .catch((error) => {
        resolve({ error: handleAuthorizationError(error) });
      });
  });
}
